import {
  BillGroupSummary,
  IQuoteBasicRespSchema,
  QuoteStatusEnum,
  QuoteTypeEnum,
} from '@monetize/types/app';
import {
  ApiListResponse,
  GetListApiConfig,
  GetListApiFilter,
} from '@monetize/types/common';
import { arrayToObject } from '@monetize/utils/core';
import { useMemo, useState } from 'react';
import { useGetAllBillGroups } from '../api/billGroupService';
import { useGetAllAccountContracts, useGetQuoteList } from '../api/cpqService';
import { useACL } from '../services/acl/acl';
import {
  buildContractLineages,
  ContractLineage,
  findLineageForContract,
  groupRenewalQuotesByLineage,
  sortContractLineages,
} from '../utils/contractLineage';
import { IContractWithBillGroup } from './AccountDetail';

export type ContractLineageWithBillGroups =
  ContractLineage<IContractWithBillGroup>;

const selectBillGroupsById = (billGroups: BillGroupSummary[]) =>
  arrayToObject(billGroups, 'id');

// Renewal quotes that have not become a contract yet, most recently worked on
// first so they are the ones shown before "+N more"
const RENEWAL_QUOTES_CONFIG: GetListApiConfig = {
  rows: 100,
  sortField: 'modifyDate',
  sortOrder: -1,
};
const RENEWAL_QUOTES_FILTERS: GetListApiFilter = {
  type: QuoteTypeEnum.RENEWAL,
  status: {
    in: [
      QuoteStatusEnum.DRAFT,
      QuoteStatusEnum.REVIEW,
      QuoteStatusEnum.APPROVED,
      QuoteStatusEnum.DENIED,
      QuoteStatusEnum.SENT,
      QuoteStatusEnum.ACCEPTED,
      QuoteStatusEnum.ACCEPTED_ADMIN_EDITABLE,
    ],
  },
};

const selectQuotes = ({ content }: ApiListResponse<IQuoteBasicRespSchema>) =>
  content;

/**
 * Every contract lineage on the account, sorted so the ones needing attention
 * come first. With `billGroupId`, only lineages that touch that bill group are
 * returned, but each is returned whole so chains that hop bill groups stay intact.
 */
export function useAccountContractLineages({
  accountId,
  billGroupId,
}: {
  accountId?: string;
  billGroupId?: string;
}) {
  // Fixed per mount so memoized lineages do not churn on every render
  const [today] = useState(() => new Date());

  const {
    data: contracts,
    isLoading: isContractsLoading,
    isError,
    refetch,
  } = useGetAllAccountContracts(accountId ?? '', {
    meta: { showErrorToast: true },
  });

  // Same key as the contracts table uses, so the cache is shared
  const { data: billGroupsById, isLoading: isBillGroupsLoading } =
    useGetAllBillGroups(
      { filters: { accountId: accountId ?? '' } },
      { enabled: !!accountId, select: selectBillGroupsById },
    );

  const lineages = useMemo((): ContractLineageWithBillGroups[] => {
    if (!contracts) {
      return [];
    }
    const contractsWithBillGroup = contracts.map(
      (contract): IContractWithBillGroup => ({
        ...contract,
        billGroup: billGroupsById?.[contract.billGroupId],
      }),
    );
    const allLineages = buildContractLineages(contractsWithBillGroup, today);
    return sortContractLineages(
      billGroupId
        ? allLineages.filter(({ billGroupIds }) =>
            billGroupIds.includes(billGroupId),
          )
        : allLineages,
    );
  }, [contracts, billGroupsById, billGroupId, today]);

  return {
    lineages,
    today,
    totalContracts: contracts?.length ?? 0,
    isLoading: isContractsLoading || isBillGroupsLoading,
    /** Lets callers tell a failed fetch apart from an account with no contracts */
    isError,
    refetch,
  };
}

/**
 * The lineage that contains `contractId`. Shares its cache with the account's
 * contract lineages, so opening a contract from the account tab is free.
 */
export function useContractLineage({
  contractId,
  accountId,
}: {
  contractId: string;
  accountId?: string;
}) {
  const { lineages, today, isLoading } = useAccountContractLineages({
    accountId,
  });

  const lineage = useMemo(
    () => findLineageForContract(lineages, contractId),
    [lineages, contractId],
  );

  return { lineage, today, isLoading };
}

/**
 * The account's renewal quotes in progress, keyed by the id of the lineage they
 * renew. One query per account, so the cards and the contract modal share it.
 */
export function useRenewalQuotesByLineage({
  accountId,
  lineages,
}: {
  accountId?: string;
  lineages: ContractLineageWithBillGroups[];
}) {
  const { canDo } = useACL();
  const { data: renewalQuotes } = useGetQuoteList(
    {
      config: RENEWAL_QUOTES_CONFIG,
      filters: RENEWAL_QUOTES_FILTERS,
      accountId,
    },
    {
      enabled: !!accountId && canDo([['sales', 'read']]),
      select: selectQuotes,
    },
  );

  return useMemo(
    () => groupRenewalQuotesByLineage(lineages, renewalQuotes ?? []),
    [lineages, renewalQuotes],
  );
}
